import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLoginPath } from '../utils/branding';
import Step1CustomerSelection from './UserManagement/Step1CustomerSelection';
import Step2ContactSelection from './UserManagement/Step2ContactSelection';
import Step3ReviewCreate from './UserManagement/Step3ReviewCreate';
import AdminUserCreate from './UserManagement/AdminUserCreate';
import ManageUsers from './UserManagement/ManageUsers';
import UserAssignments from './UserManagement/UserAssignments';
import { CustomerV2, CustomerWithContact, userManagementService } from '../services/userManagementService';
import type { UserAccessLevel } from '../services/authService';

const steps = ['Select Customers', 'Select Contact Info', 'Review & Create'];

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Set initial tab based on access level
  const [activeTab, setActiveTab] = useState(() => {
    // Support users start on "Manage Users" tab (index 0 for them)
    // All_access users start on "Create User" tab (index 0 for them)
    return 0;
  });
  const [userType, setUserType] = useState<'normal' | 'admin'>('normal');
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerV2[]>([]);
  const [customersWithContact, setCustomersWithContact] = useState<CustomerWithContact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [createdUserEmail, setCreatedUserEmail] = useState<string | null>(null);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);

  // Check if user has access (all_access, admin, or support)
  useEffect(() => {
    if (user && user.access_level !== 'all_access' && user.access_level !== 'admin' && user.access_level !== 'support') {
      // Redirect to dashboard if not all_access, admin, or support user
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleNext = () => {
    setError(null);
    if (activeStep === 0) {
      // Validate that at least one customer is selected
      if (selectedCustomers.length === 0) {
        setError('Please select at least one customer');
        return;
      }
      // Prepare customers with contact fields
      const customersWithFields: CustomerWithContact[] = selectedCustomers.map((customer) => ({
        customerId: customer.id,
        customerName: customer.red_rock_full_entity_name || customer.company_name || 'Unknown',
        availableFields: [],
      }));
      setCustomersWithContact(customersWithFields);
    } else if (activeStep === 1) {
      // Validate that all customers have selected contact fields
      const incomplete = customersWithContact.find(
        (c) => !c.selectedField || !c.selectedValue
      );
      if (incomplete) {
        setError(`Please select contact information for ${incomplete.customerName}`);
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedCustomers([]);
    setCustomersWithContact([]);
    setError(null);
    setSuccess(false);
    setCreatedUserId(null);
    setCreatedUserEmail(null);
    setInvitationSent(false);
  };

  const handleUserTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newUserType: 'normal' | 'admin' | null,
  ) => {
    if (newUserType !== null) {
      setUserType(newUserType);
      handleReset(); // Reset form when switching user types
    }
  };

  const handleCreateSuccess = (userId: string, userEmail: string) => {
    setSuccess(true);
    setCreatedUserId(userId);
    setCreatedUserEmail(userEmail);
    // Only set activeStep for normal users (who have a stepper)
    if (userType === 'normal') {
      setActiveStep(2); // Stay on review step to show success
    }
  };

  const handleSendInvitation = async () => {
    if (!createdUserEmail) {
      setError('User email not found');
      return;
    }

    try {
      setSendingInvitation(true);
      setError(null);
      await userManagementService.requestPasswordReset(createdUserEmail);
      setInvitationSent(true);
      setSuccess(true);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      setError(error.message || 'Failed to send invitation email');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset create user form when switching to create user tab (only for all_access)
    if (user?.access_level === 'all_access' && newValue === 0) {
      handleReset();
    }
  };

  // Calculate tab indices based on user access level
  const getTabIndex = (tabName: 'create' | 'manage' | 'assignments'): number => {
    if (user?.access_level === 'support') {
      // Support users: 0 = Manage Users, 1 = User Assignments
      return tabName === 'manage' ? 0 : 1;
    } else if (user?.access_level === 'admin') {
      // Admin users: 0 = Manage Users, 1 = User Assignments (no create tab)
      return tabName === 'manage' ? 0 : 1;
    } else {
      // All_access users: 0 = Create User, 1 = Manage Users, 2 = User Assignments
      return tabName === 'create' ? 0 : tabName === 'manage' ? 1 : 2;
    }
  };

  // Show loading if checking access
  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Redirect if not all_access, admin, or support (handled by useEffect, but show message briefly)
  if (user.access_level !== 'all_access' && user.access_level !== 'admin' && user.access_level !== 'support') {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              logout();
              navigate(getLoginPath(), { replace: true });
            }}
            sx={{ ml: 2 }}
          >
            Logout
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {user.access_level === 'all_access' && <Tab label="Create User" />}
            <Tab label="Manage Users" />
            {(user.access_level === 'all_access' || user.access_level === 'admin' || user.access_level === 'support') && (
              <Tab label="User Assignments" />
            )}
          </Tabs>
        </Box>

        {user.access_level === 'all_access' && activeTab === getTabIndex('create') ? (
          <>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
              Create Client User
            </Typography>

            {/* User Type Selector */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  User Type:
                </Typography>
                <ToggleButtonGroup
                  value={userType}
                  exclusive
                  onChange={handleUserTypeChange}
                  aria-label="user type selection"
                  size="small"
                >
                  <ToggleButton value="normal" aria-label="normal user">
                    Normal User
                  </ToggleButton>
                  <ToggleButton value="admin" aria-label="admin user">
                    Admin/Support User
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {userType === 'admin' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Admin, Support, and All_Access users have system-wide access and do not require customer assignments.
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* Conditional rendering based on user type */}
            {userType === 'normal' ? (
              <>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Client user created successfully!</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      User ID: {createdUserId}
                      {createdUserEmail && ` | Email: ${createdUserEmail}`}
                    </Typography>
                    {invitationSent ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          ✅ Password reset email has been sent to {createdUserEmail}. 
                          The user can now set their password via the link in the email.
                        </Typography>
                      </Alert>
                    ) : createdUserEmail ? (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EmailIcon />}
                          onClick={handleSendInvitation}
                          disabled={sendingInvitation}
                          size="small"
                        >
                          {sendingInvitation ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Sending...
                            </>
                          ) : (
                            'Send Password Reset Email'
                          )}
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                          Send an invitation email to the user so they can set their password.
                        </Typography>
                      </Box>
                    ) : null}
                  </Alert>
                )}

                <Box sx={{ mt: 4 }}>
                  {activeStep === 0 && (
                    <Step1CustomerSelection
                      selectedCustomers={selectedCustomers}
                      onSelectionChange={setSelectedCustomers}
                    />
                  )}

                  {activeStep === 1 && (
                    <Step2ContactSelection
                      customersWithContact={customersWithContact}
                      onCustomersChange={setCustomersWithContact}
                      selectedCustomers={selectedCustomers}
                    />
                  )}

                  {activeStep === 2 && (
                    <Step3ReviewCreate
                      customersWithContact={customersWithContact}
                      selectedCustomers={selectedCustomers}
                      onSuccess={handleCreateSuccess}
                      onError={setError}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  <Box>
                    {activeStep === steps.length - 1 ? (
                      <>
                        <Button onClick={handleReset} sx={{ mr: 1 }}>
                          Create Another
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => navigate('/dashboard')}
                        >
                          Go to Dashboard
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>User created successfully!</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      User ID: {createdUserId}
                      {createdUserEmail && ` | Email: ${createdUserEmail}`}
                    </Typography>
                    {invitationSent ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          ✅ Password reset email has been sent to {createdUserEmail}. 
                          The user can now set their password via the link in the email.
                        </Typography>
                      </Alert>
                    ) : createdUserEmail ? (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EmailIcon />}
                          onClick={handleSendInvitation}
                          disabled={sendingInvitation}
                          size="small"
                        >
                          {sendingInvitation ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Sending...
                            </>
                          ) : (
                            'Send Password Reset Email'
                          )}
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                          Send an invitation email to the user so they can set their password.
                        </Typography>
                      </Box>
                    ) : null}
                  </Alert>
                )}

                <Box sx={{ mt: 4 }}>
                  <AdminUserCreate
                    onSuccess={handleCreateSuccess}
                    onError={setError}
                  />
                </Box>

                {success && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                    <Button onClick={handleReset} variant="outlined">
                      Create Another
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        ) : activeTab === getTabIndex('manage') ? (
          <ManageUsers
            currentUserAccessLevel={user.access_level as UserAccessLevel | undefined}
            currentUserId={user.id}
          />
        ) : activeTab === getTabIndex('assignments') ? (
          <UserAssignments userAccessLevel={user.access_level as UserAccessLevel | undefined} />
        ) : null}
      </Paper>
    </Container>
  );
};

export default UserManagement;

